import { BALANCE } from '../config/balance';

/**
 * The head-tilt gesture: a big, deliberate roll held for a moment.
 *
 * It is the player saying "settle me onto this wall". A spike that falls back
 * inside the threshold is tracker noise or a quick glance, so the roll has to
 * stay past it for `tiltHoldTime` before the gesture fires. The watcher re-arms
 * as soon as the head comes back to upright, so each swivel needs its own
 * deliberate tilt.
 *
 * The direction of the tilt is deliberately not checked: the facial transform
 * matrix's roll sign still needs a real webcam to confirm (`Q6`), and there is
 * only one plane to settle onto, so either tilt means the same thing.
 */
export class HeadTiltWatcher {
  private held = 0;
  /** False from the moment a tilt fires until the head comes back upright. */
  private armed = true;

  constructor(
    private readonly threshold = BALANCE.camera.swivel.tiltThreshold,
    private readonly holdTime = BALANCE.camera.swivel.tiltHoldTime,
  ) {}

  /**
   * Feed this frame's head roll, radians. Returns true on the frame the
   * gesture completes; leaning after that does not fire again until the head
   * has been upright.
   */
  update(roll: number, dt: number): boolean {
    if (Math.abs(roll) < this.threshold) {
      this.held = 0;
      this.armed = true;
      return false;
    }
    if (!this.armed) return false;
    this.held += dt;
    if (this.held < this.holdTime) return false;
    this.held = 0;
    this.armed = false;
    return true;
  }

  /** Forget a half-finished tilt, as starting a run does. */
  reset(): void {
    this.held = 0;
    this.armed = true;
  }

  /** Seconds the tilt has been held, for tests and tooling. */
  get heldFor(): number {
    return this.held;
  }
}
